import { t } from "@/i18n/pl";
import { DAY_ABBR } from "@/lib/format";
import { cn } from "@/lib/utils";

/**
 * Read-only weekday strip: which days a car drives, dimmed-struck when full.
 * Small enough for cards, self-explanatory without a legend.
 */
export function DayChips({
  days,
  availability,
  muted = false,
  className,
}: {
  days: number[];
  /** day -> free seats; omit to render plain schedule chips. */
  availability?: Record<number, number>;
  /** Navy-surface variant. */
  muted?: boolean;
  className?: string;
}) {
  return (
    <span className={cn("flex items-center gap-1", className)} role="list">
      {days.map((day) => {
        const free = availability?.[day];
        const isFull = free !== undefined && free === 0;
        return (
          <span
            key={day}
            role="listitem"
            title={free !== undefined ? t.days.freeOn(DAY_ABBR[day] ?? "", free) : DAY_ABBR[day]}
            className={cn(
              "rounded-md px-1.5 py-0.5 text-[11px] font-semibold",
              muted
                ? "bg-sidebar-accent text-sidebar-foreground"
                : "bg-secondary text-secondary-foreground",
              isFull && "line-through opacity-45",
            )}
          >
            {DAY_ABBR[day]}
          </span>
        );
      })}
    </span>
  );
}

/** Toggleable weekday picker for forms (join request, group create/edit). */
export function DayPicker({
  allowed,
  selected,
  onChange,
  disabled = [],
  idPrefix = "day",
}: {
  /** Days that can be picked at all (e.g. the group's driving days). */
  allowed: number[];
  selected: number[];
  onChange: (days: number[]) => void;
  /** Shown but not pickable — the viewer is already spoken for on these days. */
  disabled?: number[];
  idPrefix?: string;
}) {
  function toggle(day: number) {
    onChange(
      selected.includes(day)
        ? selected.filter((d) => d !== day)
        : [...selected, day].sort((a, b) => a - b),
    );
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {allowed.map((day) => {
        const active = selected.includes(day);
        const blocked = disabled.includes(day);
        return (
          <button
            key={day}
            id={`${idPrefix}-${day}`}
            type="button"
            aria-pressed={active}
            disabled={blocked}
            onClick={() => toggle(day)}
            className={cn(
              "min-w-11 rounded-lg border px-2 py-2 text-sm font-semibold transition-colors",
              active
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-card text-foreground hover:bg-accent",
              blocked && "cursor-not-allowed line-through opacity-45",
            )}
          >
            {DAY_ABBR[day]}
          </button>
        );
      })}
    </div>
  );
}
